// src/utils/loginSecurity.js
// Login security utilities for rate limiting and brute force protection

const LOGIN_ATTEMPTS_KEY = 'login_attempts';
const LOCKOUT_KEY = 'login_lockout';
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
const ATTEMPT_WINDOW = 5 * 60 * 1000; // 5 minutes

/**
 * Get login attempts from localStorage
 */
const getLoginAttempts = () => {
  try {
    const data = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    if (!data) return [];

    const attempts = JSON.parse(data);
    const now = Date.now();

    // Filter out attempts older than the attempt window
    return attempts.filter(timestamp => now - timestamp < ATTEMPT_WINDOW);
  } catch (error) {
    console.error('Error reading login attempts:', error);
    return [];
  }
};

/**
 * Record a failed login attempt
 */
export const recordFailedAttempt = () => {
  try {
    const attempts = getLoginAttempts();
    attempts.push(Date.now());
    localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(attempts));

    // Check if we should lock the account
    if (attempts.length >= MAX_ATTEMPTS) {
      const lockoutUntil = Date.now() + LOCKOUT_DURATION;
      localStorage.setItem(LOCKOUT_KEY, lockoutUntil.toString());
      return {
        locked: true,
        remainingTime: LOCKOUT_DURATION,
        attempts: attempts.length
      };
    }

    return {
      locked: false,
      attempts: attempts.length,
      remainingAttempts: MAX_ATTEMPTS - attempts.length
    };
  } catch (error) {
    console.error('Error recording failed attempt:', error);
    return { locked: false, attempts: 0 };
  }
};

/**
 * Clear login attempts after successful login
 */
export const clearLoginAttempts = () => {
  try {
    localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
    localStorage.removeItem(LOCKOUT_KEY);
  } catch (error) {
    console.error('Error clearing login attempts:', error);
  }
};

/**
 * Check if account is currently locked
 */
export const isAccountLocked = () => {
  try {
    const lockoutUntil = localStorage.getItem(LOCKOUT_KEY);
    if (!lockoutUntil) return { locked: false };

    const lockoutTime = parseInt(lockoutUntil, 10);
    const now = Date.now();

    if (now < lockoutTime) {
      const remainingTime = lockoutTime - now;
      return {
        locked: true,
        remainingTime,
        remainingMinutes: Math.ceil(remainingTime / 60000)
      };
    } else {
      // Lockout expired, clear it
      clearLoginAttempts();
      return { locked: false };
    }
  } catch (error) {
    console.error('Error checking account lock:', error);
    return { locked: false };
  }
};

/**
 * Get remaining login attempts
 */
export const getRemainingAttempts = () => {
  const attempts = getLoginAttempts();
  return Math.max(0, MAX_ATTEMPTS - attempts.length);
};

/**
 * Validate reCAPTCHA score (client-side check)
 * @param {number} score - reCAPTCHA score (0.0 - 1.0)
 * @returns {boolean} - Whether the score is acceptable
 */
export const validateRecaptchaScore = (score) => {
  // Recommended threshold is 0.5
  // Higher score = more likely human
  // Lower score = more likely bot
  return score >= 0.5;
};

/**
 * Generate device fingerprint (basic implementation)
 */
export const getDeviceFingerprint = () => {
  const userAgent = navigator.userAgent;
  const screenResolution = `${window.screen.width}x${window.screen.height}`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const language = navigator.language;

  return btoa(`${userAgent}-${screenResolution}-${timezone}-${language}`);
};

/**
 * Check for suspicious activity patterns
 */
export const detectSuspiciousActivity = () => {
  const attempts = getLoginAttempts();

  // Check for rapid-fire attempts (more than 3 in 30 seconds)
  if (attempts.length >= 3) {
    const recentAttempts = attempts.filter(
      timestamp => Date.now() - timestamp < 30000
    );
    if (recentAttempts.length >= 3) {
      return {
        suspicious: true,
        reason: 'Too many rapid attempts detected'
      };
    }
  }

  return { suspicious: false };
};

const loginSecurity = {
  recordFailedAttempt,
  clearLoginAttempts,
  isAccountLocked,
  getRemainingAttempts,
  validateRecaptchaScore,
  getDeviceFingerprint,
  detectSuspiciousActivity
};

export default loginSecurity;
